import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { Button } from '@/components/ui/button';
import { UserIcon, CameraIcon, ChartBarIcon, UserGroupIcon, ShieldCheckIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

const SimpleStartSection = () => {
  const { openModal } = useEarlyAdopter();

  const handleCTA = () => {
    openModal('Homepage Simple Start - Request Early Access');
  };

  const steps = [
    {
      number: 1,
      icon: UserIcon,
      title: 'Create Your Profile',
      description: 'Secure, private, and designed around how you live.'
    },
    {
      number: 2,
      icon: CameraIcon,
      title: 'Start a Home Safety Check',
      description: 'Get a clear picture of how your home supports your independence today.'
    },
    {
      number: 3,
      icon: ChartBarIcon,
      title: 'See Your ThriveScore',
      description: 'Understand where you stand, and what\'s helping you stay there.'
    }
  ];

  return (
    <section className="py-24 md:py-32 bg-gradient-to-b from-background via-secondary/25 to-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Upgrade How You Live at Home, Starting Today
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Three simple steps to more confident living at home.
          </p>
        </div>

        {/* Steps - Card layout with connecting line */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="relative">
            {/* Connecting line - hidden on mobile */}
            <div className="hidden md:block absolute top-1/2 left-[10%] right-[10%] h-px bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 -translate-y-1/2" />
            
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {steps.map((step) => (
                <div 
                  key={step.number} 
                  className="relative z-10 bg-card rounded-md p-6 shadow-raised hover:shadow-floating hover:-translate-y-0.5 transition-all duration-300"
                >
                  {/* Step label */}
                  <p className="text-xs font-bold text-primary uppercase tracking-wider mb-4">
                    Step {step.number}
                  </p>
                  
                  {/* Icon + Title row */}
                  <div className="flex items-center gap-3 mb-3">
                    <step.icon className="w-6 h-6 text-muted-foreground" />
                    <h3 className="text-lg font-bold text-foreground">
                      {step.title}
                    </h3>
                  </div>
                  
                  {/* Description */}
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pills/Tags */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/50 text-foreground text-sm rounded-full border border-border/50">
            <UserGroupIcon className="w-6 h-6 text-primary" />
            Designed for older adults
          </span>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/50 text-foreground text-sm rounded-full border border-border/50">
            <ShieldCheckIcon className="w-6 h-6 text-primary" />
            Private by design
          </span>
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/50 text-foreground text-sm rounded-full border border-border/50">
            <DevicePhoneMobileIcon className="w-6 h-6 text-primary" />
            Works on your phone, tablet, or computer
          </span>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={handleCTA}
            size="lg"
            className="text-lg px-10 py-6 min-h-[56px] rounded-sm font-bold shadow-raised hover:shadow-floating transition-shadow mb-4"
          >
            Get Early Access
          </Button>
          <p className="text-muted-foreground">
            No commitment required. We'll invite you when your spot opens.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SimpleStartSection;
